/**
 * Admin → Integrations → Email Sender card
 *
 * Lets admins change the From-address and From-name used by all outbound
 * emails (property alerts, order receipts, etc.), preview the DNS records
 * Resend expects for domain verification, and fire a test send.
 *
 * Backend endpoints: GET/PUT /api/admin/email-settings and
 * POST /api/admin/email-settings/test  (see routes/shopper_engagement.py).
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, Mail, Save, Send, ShieldCheck, ShieldAlert, Copy, Info,
} from "lucide-react";
import {
  Card, CardHeader, CardTitle, CardDescription, CardContent,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { getApiUrl } from "@/utils/api";
import { IntegrationApiLogToggle } from "./IntegrationApiLogToggle";

const API = getApiUrl();

function copy(value) {
  if (!value || !navigator?.clipboard) return;
  navigator.clipboard.writeText(value).then(
    () => toast.success("Copied"),
    () => toast.error("Copy failed"),
  );
}

export default function EmailSenderCard() {
  const [state, setState] = useState({
    email: "", name: "", sender_domain: "",
    env_fallback: "", is_default_sandbox: false,
    resend_configured: false, dns_records: [],
  });
  const [initial, setInitial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [testing, setTesting] = useState(false);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/email-settings`, { withCredentials: true });
      setState(data);
      setInitial({ email: data.email || "", name: data.name || "" });
    } catch {
      toast.error("Failed to load email settings");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const dirty = initial && (initial.email !== state.email || initial.name !== state.name);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(
        `${API}/admin/email-settings`,
        { sender_email: state.email, sender_name: state.name },
        { withCredentials: true },
      );
      setState(data);
      setInitial({ email: data.email || "", name: data.name || "" });
      toast.success("Sender saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    if (!testEmail || !testEmail.includes("@")) {
      toast.error("Enter a valid recipient email");
      return;
    }
    setTesting(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/email-settings/test`,
        { to: testEmail },
        { withCredentials: true },
      );
      toast.success(`Test sent to ${data.to} from ${data.from}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setTesting(false);
    }
  };

  const domain = state.sender_domain;
  const verified = !state.is_default_sandbox && !!domain;

  return (
    <Card className="border" data-testid="email-sender-card">
      <CardHeader>
        <IntegrationApiLogToggle provider="resend" />
        <div className="flex items-start justify-between gap-2 pt-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" /> Email Sender
            </CardTitle>
            <CardDescription className="mt-1">
              The From-address and name every automated email is sent from —
              order receipts, property alerts, transactional notices.
            </CardDescription>
          </div>
          <div
            className={`text-xs px-2 py-1 rounded-full font-medium flex items-center gap-1 ${
              !state.resend_configured
                ? "bg-red-50 text-red-700 border border-red-200"
                : verified
                  ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                  : "bg-amber-50 text-amber-800 border border-amber-200"
            }`}
            data-testid="email-sender-status"
          >
            {!state.resend_configured ? (
              <>
                <ShieldAlert className="h-3.5 w-3.5" />
                Resend key not set
              </>
            ) : verified ? (
              <>
                <ShieldCheck className="h-3.5 w-3.5" />
                Custom domain
              </>
            ) : (
              <>
                <ShieldAlert className="h-3.5 w-3.5" />
                Using sandbox sender
              </>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-6">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : (
          <>
            {/* --- From identity --- */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>From address</Label>
                <Input
                  type="email"
                  value={state.email}
                  onChange={(e) => setState((s) => ({ ...s, email: e.target.value }))}
                  placeholder="alerts@selllocalhomes.com"
                  data-testid="email-sender-address"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Blank falls back to <code className="bg-muted/60 px-1 rounded">{state.env_fallback || "onboarding@resend.dev"}</code>.
                </p>
              </div>
              <div>
                <Label>From name</Label>
                <Input
                  value={state.name}
                  onChange={(e) => setState((s) => ({ ...s, name: e.target.value }))}
                  placeholder="Sell Local Homes"
                  data-testid="email-sender-name"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Blank uses the site brand name.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button onClick={save} disabled={!dirty || saving} data-testid="email-sender-save">
                {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving…</> : <><Save className="mr-2 h-4 w-4" />Save</>}
              </Button>
              {dirty && <span className="text-xs text-muted-foreground">Unsaved changes</span>}
            </div>

            {/* --- Sandbox warning + DNS records --- */}
            {state.is_default_sandbox && (
              <div className="rounded-md border border-amber-200 bg-amber-50/60 p-3 text-sm">
                <div className="flex items-start gap-2 text-amber-900">
                  <Info className="h-4 w-4 mt-0.5 shrink-0" />
                  <div>
                    <strong>You&apos;re on Resend&apos;s sandbox sender.</strong> Emails only reach the
                    address you registered in Resend. Verify a custom domain (steps below) and
                    swap the From-address to <code className="bg-white/70 px-1 rounded">alerts@yourdomain.com</code> to reach real customers.
                  </div>
                </div>
              </div>
            )}

            {domain && (
              <div className="space-y-2">
                <div className="text-sm font-medium">DNS records to verify <code className="bg-muted/60 px-1 rounded">{domain}</code> in Resend</div>
                <p className="text-xs text-muted-foreground">
                  Add these three records to your DNS provider (Cloudflare, Route53, Namecheap, etc.),
                  then click <em>Verify</em> in your Resend dashboard. Once verified, emails from
                  <code className="bg-muted/60 px-1 rounded"> {state.email}</code> will be signed and delivered.
                </p>
                <div className="border border-border rounded overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-16">Type</TableHead>
                        <TableHead>Host</TableHead>
                        <TableHead>Value</TableHead>
                        <TableHead className="w-24">Purpose</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {state.dns_records.map((r, i) => (
                        <TableRow key={i}>
                          <TableCell className="font-mono text-xs">{r.type}</TableCell>
                          <TableCell className="font-mono text-xs">
                            <div className="flex items-center gap-1">
                              <code className="truncate">{r.host}</code>
                              <button type="button" onClick={() => copy(r.host)} className="text-muted-foreground hover:text-foreground">
                                <Copy className="h-3 w-3" />
                              </button>
                            </div>
                          </TableCell>
                          <TableCell className="font-mono text-xs">
                            <div className="flex items-center gap-1">
                              <code className="truncate">{r.value}</code>
                              <button type="button" onClick={() => copy(r.value)} className="text-muted-foreground hover:text-foreground">
                                <Copy className="h-3 w-3" />
                              </button>
                            </div>
                            {r.priority ? <div className="text-muted-foreground">priority {r.priority}</div> : null}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">{r.purpose}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <p className="text-xs text-muted-foreground">
                  Full setup docs:{" "}
                  <a
                    href="https://resend.com/domains"
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary hover:underline"
                  >
                    resend.com/domains
                  </a>
                </p>
              </div>
            )}

            {/* --- Test send --- */}
            <div className="pt-4 border-t border-border space-y-2">
              <Label>Send a test email</Label>
              <div className="flex gap-2">
                <Input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="flex-1"
                  data-testid="email-sender-test-address"
                />
                <Button
                  onClick={sendTest}
                  disabled={testing || !state.resend_configured}
                  data-testid="email-sender-test-btn"
                >
                  {testing ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Sending…</> : <><Send className="mr-2 h-4 w-4" />Send test</>}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Uses the currently-saved From identity. If it doesn&apos;t arrive, check the
                Resend dashboard for a bounce.
              </p>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
